import React from 'react';
import { View, Pressable, Platform } from 'react-native';
import { Image } from 'expo-image';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';

export const formatBlogDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const estimateReadingTime = (html, md) => {
  const source = html || md || '';
  const text = source.replace(/<[^>]*>/g, '');
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
};

export const BlogPostCard = React.memo(({ post, isDark, isXS, isSM, onPress, isFeatured = false }) => {
  const readTime = estimateReadingTime(post.contentHtml, post.contentMd);

  return (
    <Pressable
      onPress={() => onPress(post.slug)}
      accessibilityRole="link"
      accessibilityLabel={post.title}
      style={({ hovered }) => ({
        borderRadius: 20,
        overflow: 'hidden',
        backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        borderWidth: 1,
        borderColor: hovered
          ? (isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)')
          : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'),
        transition: 'all 0.25s ease',
        transform: hovered ? [{ translateY: -2 }] : [],
        ...(Platform.OS === 'web' && {
          cursor: 'pointer',
        }),
      })}
    >
      {post.coverImage ? (
        <View style={{ width: '100%', aspectRatio: isFeatured ? 16 / 9 : 16 / 10, overflow: 'hidden' }}>
          <Image
            source={{ uri: post.coverImage }}
            style={{ width: '100%', height: '100%' }}
            contentFit="cover"
            transition={300}
            accessibilityLabel={post.title}
          />
          {post.category && (
            <View
              style={{
                position: 'absolute',
                top: 12,
                left: 12,
                paddingHorizontal: 10,
                paddingVertical: 5,
                borderRadius: 8,
                backgroundColor: 'rgba(0,0,0,0.55)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
              }}
            >
              <Text size="xs" weight="medium" style={{ color: '#fff' }}>
                {post.category}
              </Text>
            </View>
          )}
        </View>
      ) : (
        <View
          style={{
            width: '100%',
            aspectRatio: isFeatured ? 16 / 9 : 16 / 10,
            backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name="newspaper" size={32} color={isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'} />
          {post.category && (
            <View
              style={{
                position: 'absolute',
                top: 12,
                left: 12,
                paddingHorizontal: 10,
                paddingVertical: 5,
                borderRadius: 8,
                backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
              }}
            >
              <Text size="xs" weight="medium" color="secondary">
                {post.category}
              </Text>
            </View>
          )}
        </View>
      )}

      <View style={{ padding: isXS ? 16 : 20 }}>
        <Text
          weight="semibold"
          numberOfLines={2}
          style={{
            fontSize: isFeatured ? (isXS ? 20 : 24) : (isXS ? 17 : 19),
            lineHeight: isFeatured ? (isXS ? 26 : 32) : (isXS ? 24 : 26),
            marginBottom: 8,
            letterSpacing: -0.3,
          }}
        >
          {post.title}
        </Text>

        {post.excerpt && (
          <Text
            size="s"
            color="secondary"
            numberOfLines={isFeatured ? 3 : 2}
            style={{ lineHeight: 22, marginBottom: 12 }}
          >
            {post.excerpt}
          </Text>
        )}

        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {post.author && (
            <Text size="xs" weight="medium" color="secondary">
              {post.author}
            </Text>
          )}
          {post.author && post.publishedAt && (
            <Text size="xs" color="tertiary">·</Text>
          )}
          {post.publishedAt && (
            <Text size="xs" color="tertiary">
              {formatBlogDate(post.publishedAt)}
            </Text>
          )}
          <Text size="xs" color="tertiary">·</Text>
          <Text size="xs" color="tertiary">
            {readTime} min read
          </Text>
        </View>

        {post.tags?.length > 0 && (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
            {post.tags.slice(0, 3).map((tag) => (
              <View
                key={tag}
                style={{
                  paddingHorizontal: 8,
                  paddingVertical: 3,
                  borderRadius: 6,
                  backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                }}
              >
                <Text size="xxs" color="tertiary">{tag}</Text>
              </View>
            ))}
          </View>
        )}
      </View>
    </Pressable>
  );
});

BlogPostCard.displayName = 'BlogPostCard';
